import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Languages, Lock, Menu, Moon, Search, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Sidebar } from '@/components/sidebar/sidebar';
import { Breadcrumb } from '@/components/breadcrumb';
import { AirportScopePicker } from './airport-scope-picker';
import { AvatarMenu } from './avatar-menu';
import { NotificationBell } from './notification-bell';
import { NexaLockup } from '@/components/nexa-mark/nexa-lockup';
import { SUPPORTED_LANGUAGES } from '@/i18n';
import { DensityControl } from '@/components/density-control';
import { FullscreenButton } from '@/components/fullscreen/fullscreen-button';
import { useFocusMode } from '@/components/focus-mode';
import { cn } from '@/lib/cn';
import { useColorScheme } from '@/theme/use-color-scheme';
import { lockScreen } from '@/auth/screen-lock';

// Top bar per docs/ui/operator-ui-tds.md §4.3. Brand lockup + Cmd+K placeholder +
// language toggle + light/dark toggle + notification bell + full screen + screen lock +
// avatar dropdown.
//
// The bell is the console's ONE notification surface (run notifications + operator
// alerts, operator-notifications U2). It stands here on every console page — phone
// included — and at the foot of the icon rail on «Operar», which has no top bar
// (`sidebar-rail.tsx`): one bell per screen, never two.
//
// Identity in the chrome: the Nexa lockup alone carries the product — no
// "Operator Console" caption beside it (the surface is obvious from the app you
// are in, and the words only crowded the bar). The pinned airline follows as a
// chip, never as the leading logo, which used to make the console look like an
// airline's own app. Still no tenant selector (CLAUDE.md anti-patterns): the chip
// is a label. The console name lives on in the document title and the link's
// accessible name.

const TENANT = import.meta.env.VITE_TENANT ?? 'tenant';

// Tenant logo URL resolved at build time per docs/ui/operator-ui-tds.md §3.3
// (NEX-145). VITE_TENANT is fixed per deploy; the file lives in
// public/tenant/<airline>/logo.svg and is served from the public/ root.
const LOGO_SRC = `/tenant/${TENANT}/logo.svg`;

// The same mark in white for the dark scheme, when the pack ships one. Dark is a grey
// scale for every airline (theme/brand-pack.ts), so this is where the airline shows.
// `typeof` guard: tests and Storybook build without the tenant-brand plugin.
const LOGO_DARK_SRC =
  typeof __TENANT_LOGO_DARK__ !== 'undefined' && __TENANT_LOGO_DARK__
    ? `/tenant/${TENANT}/logo-dark.svg`
    : null;

/** = PASSENGER_SEARCH_INPUT_ID (routes/passengers/passenger-search). */
const SEARCH_INPUT_ID = 'dashboard-search';

function nextLanguage(current: string): string {
  const idx = SUPPORTED_LANGUAGES.indexOf(current as (typeof SUPPORTED_LANGUAGES)[number]);
  const safe = idx < 0 ? 0 : idx;
  return SUPPORTED_LANGUAGES[(safe + 1) % SUPPORTED_LANGUAGES.length] ?? 'en';
}

export function TopBar() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language ?? 'en';
  const { scheme, setPreference } = useColorScheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // «Modo foco» (declaring): the icon rail from `lg` up, so below it the menu button
  // shows on a tablet too.
  const focus = useFocusMode();

  // ⌘K / Ctrl+K anywhere → the passenger search. It is mounted on the dashboard
  // hero AND on /passengers (one component, one input id —
  // PASSENGER_SEARCH_INPUT_ID in routes/passengers/passenger-search, kept as a
  // literal here so the chrome does not import a route); when the current page
  // already carries it the input just re-focuses in place, otherwise we go home
  // and its autoFocus takes over — rAF covers both without a race.
  const goToSearch = useCallback(() => {
    const mounted = document.getElementById(SEARCH_INPUT_ID);
    if (mounted) {
      mounted.focus();
      return;
    }
    navigate('/');
    requestAnimationFrame(() => document.getElementById(SEARCH_INPUT_ID)?.focus());
  }, [navigate]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        goToSearch();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goToSearch]);

  return (
    <header className="density-topbar flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border bg-background px-4">
      <div className="flex min-w-0 items-center gap-3">
        <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn('shrink-0', focus ? 'lg:hidden' : 'md:hidden')}
              aria-label={t('admin:sidebar.mobileMenu')}
              data-testid="topbar-menu"
            >
              <Menu className="h-5 w-5" aria-hidden />
            </Button>
          </DialogTrigger>
          <DialogContent className="left-0 top-0 flex h-[100dvh] w-[min(22rem,100vw)] max-w-none translate-x-0 translate-y-0 flex-col gap-3 rounded-none p-4">
            <DialogTitle>{t('admin:sidebar.mobileMenu')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('admin:sidebar.mobileHelp')}
            </DialogDescription>
            <div className="flex flex-wrap items-center gap-2">
              <TenantChip />
              <AirportScopePicker />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                aria-label={t('topbar.search')}
                onClick={() => {
                  setMobileOpen(false);
                  goToSearch();
                }}
              >
                <Search className="h-4 w-4" aria-hidden />
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label={t('topbar.changeLanguage')}
                onClick={() => void i18n.changeLanguage(nextLanguage(currentLang))}
              >
                <Languages className="h-4 w-4" aria-hidden />
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label={t(scheme === 'dark' ? 'topbar.switchToLight' : 'topbar.switchToDark')}
                onClick={() => setPreference(scheme === 'dark' ? 'light' : 'dark')}
              >
                {scheme === 'dark' ? (
                  <Sun className="h-4 w-4" aria-hidden />
                ) : (
                  <Moon className="h-4 w-4" aria-hidden />
                )}
              </Button>
            </div>
            <Sidebar mobile onNavigate={() => setMobileOpen(false)} />
          </DialogContent>
        </Dialog>
        <Link
          to="/dashboard"
          aria-label={`Nexa ${t('app.title')}`}
          className="shrink-0 rounded-md outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <NexaLockup className="text-[#07213f] dark:text-foreground" />
        </Link>

        <div className="hidden md:block">
          <TenantChip />
        </div>

        {/* Breadcrumb lives IN the header now (NEX-299 item 1), next to the
            tenant badge — the standalone breadcrumb strip below the bar is gone.
            A divider separates it so it reads as "where am I" context. */}
        <span aria-hidden className="hidden h-5 w-px shrink-0 bg-border lg:block" />
        <div className="hidden min-w-0 lg:block">
          <Breadcrumb />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Airport/region viewing scope (NEX-310) — sits beside the search per
            operator-context S5; the selection narrows Cases + Disruptions. */}
        <div className="hidden items-center gap-2 md:flex">
          <AirportScopePicker />

          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-2"
            aria-label={`${t('topbar.search')} (⌘ K)`}
            onClick={goToSearch}
          >
            <Search className="h-4 w-4" />
            <span className="text-xs text-muted-foreground">⌘ K</span>
          </Button>

          {SUPPORTED_LANGUAGES.length > 1 ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              aria-label={t('topbar.changeLanguage')}
              onClick={() => void i18n.changeLanguage(nextLanguage(currentLang))}
            >
              <Languages className="h-4 w-4" />
            </Button>
          ) : null}

          {/* Light/dark, one click. The avatar menu also carries the full
            System/Light/Dark choice, but a preference buried in a dropdown is one
            nobody finds — and this is a control operators reach for by time of day,
            not once in Settings. The icon shows the scheme it switches TO, which is
            what makes a single-button toggle readable. Pressing it commits an
            explicit preference; going back to following the OS is the menu's job. */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            aria-label={t(scheme === 'dark' ? 'topbar.switchToLight' : 'topbar.switchToDark')}
            title={t(scheme === 'dark' ? 'topbar.switchToLight' : 'topbar.switchToDark')}
            onClick={() => setPreference(scheme === 'dark' ? 'light' : 'dark')}
          >
            {scheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          <div
            id="nexa-activity-slot"
            className="hidden h-9 w-9 shrink-0 items-center justify-center xl:flex"
            aria-hidden="true"
          />
        </div>
        <DensityControl />
        <NotificationBell />

        {/* Full screen, on every width — a tablet's browser bars are what it reclaims
            (`fullscreen/`). Not drawn where the browser cannot (an iPhone), nor in the
            installed app. */}
        <FullscreenButton />

        {/* Lock the screen now, one click, beside the operator it protects — for the
            moment they step away from the desk, without waiting for the idle lock
            (auth/screen-lock.ts). The avatar menu carries the same action in words. */}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          aria-label={t('topbar.lockScreen')}
          title={t('topbar.lockScreen')}
          data-testid="topbar-lock"
          onClick={() => lockScreen()}
        >
          <Lock className="h-4 w-4" aria-hidden />
        </Button>
        <AvatarMenu />
      </div>
    </header>
  );
}

// The pinned deploy's airline, as a chip: its own logo when the tenant brand pack
// ships one, the code alone when it doesn't (a 404'd <img> must not leave a broken
// glyph in the chrome — the code is the fallback, so nothing looks unfinished).
//
// Under `.dark` the white logo replaces the coloured one by CSS rather than by the
// scheme hook, so it is right from the first paint and follows an OS flip at dusk.
function TenantChip() {
  const [logoOk, setLogoOk] = useState(true);

  return (
    <span
      className="flex shrink-0 items-center gap-1.5 rounded bg-muted py-0.5 pl-1 pr-2 text-xs uppercase tracking-wide text-muted-foreground dark:text-foreground"
      data-testid="tenant-chip"
    >
      {logoOk ? (
        <>
          <img
            src={LOGO_SRC}
            alt=""
            className={cn('h-4 w-4 rounded-sm', LOGO_DARK_SRC && 'dark:hidden')}
            width={16}
            height={16}
            onError={() => setLogoOk(false)}
          />
          {LOGO_DARK_SRC ? (
            <img
              src={LOGO_DARK_SRC}
              alt=""
              className="hidden h-4 w-4 rounded-sm dark:block"
              width={16}
              height={16}
              data-testid="tenant-logo-dark"
              onError={() => setLogoOk(false)}
            />
          ) : null}
        </>
      ) : null}
      {TENANT}
    </span>
  );
}
